import type { JobStatus, LightsOsDocument } from '../types/document'
import { upgradeDocument } from './proposals'
import { adoptFrom, currentAccountId } from './account'
import type { DueOn, PaperKind } from './paper'
import {
  balanceDue,
  depositAmount,
  docNumber,
  lastPayment,
  normalizePaper,
  paidAmount,
  total,
} from './paper'
import type { RoofSection } from './sections'
import { sectionTotals, sectionsOf } from './sections'
import type { ApprovedScope } from './approval'
import { currentApproval, driftFrom } from './approval'
import type { Visit } from './visits'
import { finishedOn } from './visits'

/** Short id, unique within one document. */
const newId = () => Math.random().toString(36).slice(2, 10)

/**
 * Where jobs actually live.
 *
 * This replaces localStorage, which was never going to hold a season. A house
 * photo is two to four megabytes as a data URL and browsers give localStorage
 * about five in total, so the old store was full after one or two jobs and
 * every save after that silently failed. IndexedDB has no such ceiling.
 *
 * The photo is stored SEPARATELY from the document. Autosave fires on every
 * change, and rewriting three megabytes of image because someone nudged a
 * brightness slider is both slow and pointless: the picture only changes when
 * a new one is loaded. Documents are small enough to write freely.
 *
 * Everything here is best effort. A browser in private mode, or one with
 * storage disabled, throws on open; the app must keep working, just without
 * remembering anything, which is what it did before this existed.
 */

const DB_NAME = 'lightsos'
/*
 * The database this one was renamed from. Its contents are copied across on
 * the first open and the old database is then left exactly where it is: the
 * copy is cheap, and a contractor's only record of a season is not something
 * to delete on the strength of a loop that appeared to finish. See migrate().
 */
const OLD_DB_NAME = 'lightsplug'
const MIGRATED_FLAG = 'lightsos.migrated.idb.v1'
const DB_VERSION = 1
const DOCS = 'docs'
const PHOTOS = 'photos'

export interface ProjectSummary {
  id: string
  name: string
  customer: string
  address: string
  savedAt: string
  /** Small JPEG of the house, so the list is recognizable at a glance. */
  thumb?: string
  layers: number
  status: JobStatus
  /*
   * The quote total and the footage, frozen at the last save.
   *
   * Both are DERIVED from the document, so in principle the list could compute
   * them. In practice that means rendering forty jobs to get forty numbers,
   * and the list has to open instantly on a phone in a driveway. They are
   * written down at save time instead, which is also the honest thing: this is
   * what the quote said when you last looked at it.
   */
  total: number
  feet: number | null
  /** Absent on jobs saved before the quote became a document. */
  money?: JobMoney
  /** The customer's phone, which lives on the paper rather than the document. */
  phone: string
  /**
   * WHAT THIS JOB STILL NEEDS, computed here rather than stored.
   *
   * The quote total and footage above are written at save time because
   * computing them means rendering the document. These do not: they are a walk
   * over two small arrays that listProjects already has in hand, because
   * getAll returns the whole record. Computing them keeps them true after an
   * older client, or another device, wrote the job without them.
   */
  work: JobWork
  /** When somebody last moved this job's state. Empty if never. See statusAt. */
  statusAt: string
  /**
   * THE DAY THE INSTALL WAS MARKED FINISHED AT THIS HOUSE, or empty.
   *
   * Off the visits rather than stored, so it cannot disagree with the stop
   * somebody pressed Finish on. The first thing in this product that records a
   * job as done, and what turns due on completion into a date.
   */
  finishedOn: string
  /*
   * WHEN SOMEBODY IS COMING. Empty on every job nobody has scheduled, which
   * is most of them, and on every job saved before the calendar existed.
   */
  visits: Visit[]
}

/** The state of a job's measurements and its agreed scope. See lib/today.ts. */
export interface JobWork {
  /** Feet on included sections nobody has measured at the house. */
  unmeasuredFeet: number
  /** Included sections carrying no length at all. */
  missingLengths: number
  /** How many things have moved since the scope was approved. Zero if never. */
  driftCount: number
  /** Whether this job has an approved scope at all. */
  approved: boolean
}

/** What the list needs to know that the document does not carry itself. */
export interface SaveMeta {
  thumb?: string
  total?: number
  feet?: number | null
  /** The company's document letters, so the list can show LP-1001. */
  prefix?: string
}

let dbPromise: Promise<IDBDatabase> | null = null

function openNamed(name: string, version?: number): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    let req: IDBOpenDBRequest
    try {
      req = version === undefined ? indexedDB.open(name) : indexedDB.open(name, version)
    } catch (e) {
      reject(e)
      return
    }
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(DOCS)) db.createObjectStore(DOCS, { keyPath: 'id' })
      if (!db.objectStoreNames.contains(PHOTOS)) db.createObjectStore(PHOTOS)
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

const all = <T,>(store: IDBObjectStore): Promise<T[]> =>
  new Promise((resolve, reject) => {
    const r = store.getAll()
    r.onsuccess = () => resolve(r.result as T[])
    r.onerror = () => reject(r.error)
  })

const allKeys = (store: IDBObjectStore): Promise<IDBValidKey[]> =>
  new Promise((resolve, reject) => {
    const r = store.getAllKeys()
    r.onsuccess = () => resolve(r.result)
    r.onerror = () => reject(r.error)
  })

/**
 * BRING A SEASON ACROSS WHEN THE PRODUCT CHANGES ITS NAME.
 *
 * The database used to be called lightsplug. Every job, every photograph and
 * every quote a contractor has saved on this machine is in there, so the
 * rename copies rather than abandons.
 *
 * Never overwrite. A job that already exists under the new name is the newer
 * one by definition, because the only way it got there is that somebody
 * worked on it after the rename. Photographs are keyed by job id and follow
 * the same rule.
 *
 * The old database is left in place afterwards. It costs disk that the browser
 * will reclaim on its own terms, and it means a migration that went wrong in a
 * browser nobody tested is recoverable rather than final.
 */
async function migrate(db: IDBDatabase): Promise<void> {
  if (localStorage.getItem(MIGRATED_FLAG) === '1') return

  // Where it can be asked, ask: opening a database that does not exist creates
  // an empty one, and there is no reason to litter.
  if (typeof indexedDB.databases === 'function') {
    try {
      const found = await indexedDB.databases()
      if (!found.some((d) => d.name === OLD_DB_NAME)) {
        localStorage.setItem(MIGRATED_FLAG, '1')
        return
      }
    } catch {
      /* not supported in this browser after all; fall through and open it */
    }
  }

  const old = await openNamed(OLD_DB_NAME)
  try {
    if (!old.objectStoreNames.contains(DOCS)) return

    const readable = [DOCS, PHOTOS].filter((n) => old.objectStoreNames.contains(n))
    const rt = old.transaction(readable, 'readonly')
    const docs = await all<{ id: string }>(rt.objectStore(DOCS))
    const photoKeys = readable.includes(PHOTOS) ? await allKeys(rt.objectStore(PHOTOS)) : []
    const photos = readable.includes(PHOTOS) ? await all<unknown>(rt.objectStore(PHOTOS)) : []

    const wt = db.transaction(readable, 'readwrite')
    const docStore = wt.objectStore(DOCS)
    for (const d of docs) if (d && d.id) docStore.add(d)
    if (readable.includes(PHOTOS)) {
      const photoStore = wt.objectStore(PHOTOS)
      photoKeys.forEach((k, i) => photoStore.add(photos[i], k))
    }
    await new Promise<void>((resolve) => {
      // add() throws on a key that is already there, which is exactly the
      // never overwrite rule. Swallowed per record so one collision does not
      // abort the rest of somebody's season.
      wt.onabort = () => resolve()
      wt.oncomplete = () => resolve()
      wt.onerror = (e) => {
        e.preventDefault()
      }
    })
    localStorage.setItem(MIGRATED_FLAG, '1')
  } finally {
    old.close()
  }
}

function open(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise
  dbPromise = openNamed(DB_NAME, DB_VERSION).then(async (db) => {
    try {
      await migrate(db)
    } catch {
      /* the new database is open and usable, which is the part that matters */
    }
    return db
  })
  return dbPromise
}

function tx<T>(store: string, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest): Promise<T> {
  return open().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const t = db.transaction(store, mode)
        const req = fn(t.objectStore(store))
        req.onsuccess = () => resolve(req.result as T)
        req.onerror = () => reject(req.error)
      }),
  )
}

/** A document with its photo lifted out, plus the summary fields the list needs. */
export interface StoredDoc extends Omit<LightsOsDocument, 'photo'> {
  photoMeta: { width: number; height: number } | null
  savedAt: string
  thumb?: string
  total?: number
  feet?: number | null
  /** What the paper says, frozen at the last save. See jobMoney below. */
  money?: JobMoney
  /**
   * WHAT HAS NOT REACHED THE SERVER YET.
   *
   * Set on every local save and cleared only when a push has actually
   * succeeded. Not a comparison of timestamps: a phone in a driveway usually
   * has a clock that is a little wrong, and a wrong clock would silently
   * decide a job had already been sent. A flag cannot be wrong about that.
   */
  dirty?: boolean
  /** Server time of the version this copy came from, or was last pushed as. */
  syncedAt?: string
  /**
   * A TOMBSTONE, BECAUSE A DELETE HAS TO TRAVEL.
   *
   * Removing the record outright looks right and is wrong: the row is still on
   * the server, so the very next pull brings the job back, and a contractor
   * who deleted something watches it reappear. The record stays, marked, with
   * its photograph dropped, until the deletion has been pushed.
   */
  deletedAt?: string
}

/**
 * WHAT A JOB IS WORTH, AND WHAT IS STILL OUT THERE.
 *
 * Written down at save time rather than computed when the list opens, because
 * the list has to open instantly on a phone in a driveway and computing it
 * means loading forty documents to get forty numbers.
 *
 * The staleness that usually makes stored derived figures a mistake does not
 * apply: this is derived from the same document in the same call that saves
 * it, so it is exactly what the quote said when it was last touched, which is
 * also what an honest job list should say.
 */
export interface JobMoney {
  /** Quote or invoice. */
  kind: PaperKind
  /** LP-1001, ready to show. Empty until a quote has lines. */
  number: string
  /**
   * The day written on the document itself, which is not the day the job was
   * last saved. A quote dated the third and touched again on the twentieth is
   * seventeen days old to the customer and nothing at all to the file system.
   */
  dated: string
  /**
   * WHEN THE BALANCE IS DUE, and it is two different questions.
   *
   * Most of this work is billed on completion and some of it on a date the
   * contractor named. An invoice list that flattened those into one due date
   * would tell a homeowner the whole balance was due the day they got it, and
   * would tell the contractor a job was late that nobody had finished.
   */
  due: DueOn
  /** ISO yyyy-mm-dd. Empty unless due is date, and sometimes empty then too. */
  dueDate: string
  /** What was asked for up front. Zero when nothing was. Not what arrived. */
  deposit: number
  /** The day the most recent payment landed. Empty when none has. */
  lastPaid: string
  /** What the customer owes for the job, after any seasonal discount. */
  total: number
  /** The deposit, and zero unless it has actually been received. */
  paid: number
  /** Still owed. The whole total until a deposit is in hand. */
  balance: number
}

/** Reads the money off a document. Nothing here guesses at anything. */
export function jobMoney(doc: LightsOsDocument, prefix = ''): JobMoney {
  const lines = doc.quote ?? []
  // Normalized, because this runs on every save and a document that has not
  // been through the loader yet would otherwise put NaN on the job list.
  const paper = normalizePaper(doc.paper, lines)
  const paid = paidAmount(lines, paper)
  return {
    kind: paper.kind,
    number: docNumber(paper, prefix),
    dated: paper.date,
    due: paper.dueOn,
    dueDate: paper.dueDate,
    deposit: depositAmount(lines, paper),
    lastPaid: lastPayment(paper),
    total: total(lines, paper),
    paid,
    balance: balanceDue(lines, paper),
  }
}

/**
 * WHAT A JOB STILL NEEDS, off the record itself.
 *
 * Deliberately defensive at every step: this runs over every job a person has,
 * including ones saved before sections existed and ones another device wrote,
 * and a single missing array here is a job list that will not open.
 */
export function jobWork(d: {
  sections?: RoofSection[]
  approvals?: ApprovedScope[]
}): JobWork {
  const sections = sectionsOf(d)
  const totals = sectionTotals(sections)
  const approved = currentApproval(d)
  return {
    unmeasuredFeet: totals.unverifiedFeet,
    missingLengths: totals.missing,
    driftCount: approved ? driftFrom(approved, sections).length : 0,
    approved: !!approved,
  }
}

export type SaveResult = 'saved' | 'full' | 'unavailable'

export async function saveProject(
  doc: LightsOsDocument,
  meta: SaveMeta = {},
): Promise<SaveResult> {
  try {
    const { photo, ...rest } = doc

    /*
     * THE PHOTO GOES DOWN FIRST.
     *
     * These are two separate stores, and the document used to be written
     * before the image. When the image write then failed - a full quota is the
     * usual way, and a 3 MB data URL is a big ask - the database was left
     * holding a job with every run in it and no photograph. Reopen that and
     * you get the design floating on a black rectangle with the house gone,
     * which is what a contractor sees the morning after quoting.
     *
     * Photo first, and the document only if it lands. A save that fails now
     * loses the last few edits, which is recoverable; the other order loses
     * the house, which is not.
     */
    if (photo) {
      const existing = await tx<string | undefined>(PHOTOS, 'readonly', (s) => s.get(doc.id))
      // Only rewrite the image when it actually changed.
      if (existing !== photo.dataUrl) {
        await tx(PHOTOS, 'readwrite', (s) => s.put(photo.dataUrl, doc.id))
      }
    }

    const stored: StoredDoc = {
      ...rest,
      // Written on every save, so a job stamped before accounts existed picks
      // one up the next time it is touched.
      accountId: rest.accountId || currentAccountId(),
      photoMeta: photo ? { width: photo.width, height: photo.height } : null,
      savedAt: new Date().toISOString(),
      thumb: meta.thumb,
      total: meta.total,
      feet: meta.feet ?? null,
      // Derived here, from the document being written, in the same call.
      money: jobMoney(doc, meta.prefix ?? ''),
      // Every local save is unsent until a push says otherwise.
      dirty: true,
      syncedAt: (await tx<StoredDoc | undefined>(DOCS, 'readonly', (s) => s.get(doc.id)))?.syncedAt,
    }
    await tx(DOCS, 'readwrite', (s) => s.put(stored))
    return 'saved'
  } catch (err) {
    const name = err instanceof Error ? err.name : ''
    return name === 'QuotaExceededError' ? 'full' : 'unavailable'
  }
}

export async function loadProject(id: string): Promise<LightsOsDocument | null> {
  try {
    const stored = await tx<StoredDoc | undefined>(DOCS, 'readonly', (s) => s.get(id))
    if (!stored) return null
    /*
     * A job belonging to somebody else does not open, it does not merely stay
     * off the list. Two people sharing a laptop is not a hypothetical in this
     * trade, and a boundary that only the list honors is not a boundary. When
     * this moves into the database it becomes a policy on the table and this
     * line goes away; until then it lives here, where every load passes.
     */
    if (stored.accountId && stored.accountId !== currentAccountId()) return null
    const dataUrl = await tx<string | undefined>(PHOTOS, 'readonly', (s) => s.get(id))
    const { photoMeta, savedAt: _s, thumb: _t, ...rest } = stored
    /*
     * SCHEMA 4 ON THE WAY IN.
     *
     * This is the load path the app actually uses, so it is the one that
     * matters. A job saved before confirmation existed has its roof runs moved
     * out of the priced collection and into proposals here, once, and the
     * document is flagged so the contractor is asked about them rather than
     * having last season's geometry quietly re-priced for him.
     */
    const upgraded = upgradeDocument(rest as LightsOsDocument, {
      now: new Date().toISOString(),
      makeId: newId,
    })
    return {
      ...upgraded,
      status: upgraded.status ?? 'draft',
      /*
       * A stored string is not a photo until it looks like one. A failed
       * encode writes "data:," which is truthy, passes every check downstream
       * and reopens the job as a black canvas with the lights floating on it.
       */
      photo:
        dataUrl && dataUrl.startsWith('data:image/') && dataUrl.length > 1000 && photoMeta
          ? { dataUrl, width: photoMeta.width, height: photoMeta.height }
          : null,
    }
  } catch {
    return null
  }
}

export async function listProjects(): Promise<ProjectSummary[]> {
  try {
    const all = await tx<StoredDoc[]>(DOCS, 'readonly', (s) => s.getAll())
    /*
     * Meet the jobs before deciding who we are. A cleared localStorage with an
     * intact IndexedDB would otherwise mint a new stamp and filter a full
     * season off the screen. See adoptFrom.
     */
    const me = adoptFrom(all.map((d) => d.accountId))
    return all
      .filter((d) => !d.deletedAt)
      .filter((d) => !d.accountId || d.accountId === me)
      .map((d) => ({
        id: d.id,
        name: d.name || 'Untitled mockup',
        customer: d.customer ?? '',
        address: d.address ?? '',
        savedAt: d.savedAt,
        thumb: d.thumb,
        layers: d.layers?.length ?? 0,
        // Everything saved before status existed is a job in progress.
        status: d.status ?? 'draft',
        total: d.total ?? 0,
        feet: d.feet ?? null,
        // Anything saved before the quote became a document has no money on it.
        money: d.money,
        phone: d.paper?.customerPhone ?? '',
        work: jobWork(d),
        statusAt: d.statusAt ?? '',
        finishedOn: finishedOn(Array.isArray(d.visits) ? d.visits : []),
        visits: Array.isArray(d.visits) ? d.visits : [],
      }))
      .sort((a, b) => (a.savedAt < b.savedAt ? 1 : -1))
  } catch (e) {
    /*
     * AN EMPTY LIST IS THE WORST LIE THIS FILE CAN TELL.
     *
     * Returning nothing is right: the app has to keep working when the
     * database is blocked, and a contractor in a driveway cannot act on a
     * stack trace. But it renders as a season with no jobs in it, and until
     * now the reason went nowhere at all - so the one failure that looks
     * exactly like total data loss was also the one failure with no evidence.
     * The message only, never a record: nothing about a customer goes to a
     * console.
     */
    console.warn('The job list could not be read:', e instanceof Error ? e.message : e)
    return []
  }
}


/**
 * RENAME A CUSTOMER EVERYWHERE THEY APPEAR.
 *
 * A customer is not a stored record in this app; they are the jobs that carry
 * their name. So correcting a spelling, a street or a phone number means
 * writing it to every one of those jobs, and that is not a workaround for the
 * missing table - it is the same edit either way. A customer record would
 * still have to push the change onto the documents, because a quote already
 * printed has to keep saying what it said.
 */
export async function updateJobsCustomer(
  ids: string[],
  patch: { customer?: string; address?: string; phone?: string },
): Promise<number> {
  let n = 0
  for (const id of ids) {
    try {
      const d = await tx<StoredDoc | undefined>(DOCS, 'readonly', (s) => s.get(id))
      if (!d) continue
      const next: StoredDoc = { ...d }
      if (patch.customer !== undefined) next.customer = patch.customer
      if (patch.address !== undefined) next.address = patch.address
      if (patch.phone !== undefined) {
        next.paper = { ...normalizePaper(next.paper, next.quote ?? []), customerPhone: patch.phone }
      }
      // Same rule: a correction that does not travel is a correction that
      // only one device believes.
      next.savedAt = new Date().toISOString()
      next.dirty = true
      await tx(DOCS, 'readwrite', (s) => s.put(next))
      n++
    } catch {
      /* one job failing does not abandon the rest */
    }
  }
  return n
}

/* ======================================================== what sync needs ===
 *
 * Sync talks to the browser through these and never opens a transaction of its
 * own. Two files both knowing the shape of a stored job is how one of them
 * ends up writing a field the other drops.
 */

/** Everything this account has that the server has not seen. */
export async function listUnsent(): Promise<StoredDoc[]> {
  try {
    const me = currentAccountId()
    const all = await tx<StoredDoc[]>(DOCS, 'readonly', (s) => s.getAll())
    return all.filter((d) => d.dirty && (!d.accountId || d.accountId === me))
  } catch {
    return []
  }
}

/** The photo for a job, as it is stored. Null when the job has none. */
export async function photoOf(id: string): Promise<string | null> {
  try {
    return (await tx<string | undefined>(PHOTOS, 'readonly', (s) => s.get(id))) ?? null
  } catch {
    return null
  }
}

export async function putPhoto(id: string, dataUrl: string): Promise<void> {
  try {
    await tx(PHOTOS, 'readwrite', (s) => s.put(dataUrl, id))
  } catch {
    /* a full quota: the job still opens, without its picture */
  }
}

/**
 * A push landed. Clear the flag, and ONLY if nothing changed in between.
 *
 * A person keeps typing while a push is in flight. Clearing the flag blindly
 * on the way back marks that newer edit as sent when it never was, and it is
 * then lost the moment another device wins a conflict. So the version that was
 * actually sent is compared, and a job edited since simply stays dirty and
 * goes up on the next pass.
 */
export async function markSent(id: string, sentAt: string, sentSavedAt: string): Promise<void> {
  try {
    const d = await tx<StoredDoc | undefined>(DOCS, 'readonly', (s) => s.get(id))
    if (!d || d.savedAt !== sentSavedAt) return
    await tx(DOCS, 'readwrite', (s) => s.put({ ...d, dirty: false, syncedAt: sentAt }))
  } catch {
    /* it stays dirty, which is the safe direction */
  }
}

/**
 * The little picture the job list is read by.
 *
 * Written separately from the job because it arrives separately: the row comes
 * down first and the photograph after it, and the thumbnail is remade from the
 * photograph rather than sent. See lib/sync.ts.
 */
export async function setThumb(id: string, thumb: string): Promise<void> {
  try {
    const d = await tx<StoredDoc | undefined>(DOCS, 'readonly', (s) => s.get(id))
    if (!d) return
    // Not marked dirty: a thumbnail is derived, so this is not a change to the
    // job and must not send it straight back up.
    await tx(DOCS, 'readwrite', (s) => s.put({ ...d, thumb }))
  } catch {
    /* a blank tile, and nothing worse */
  }
}

/**
 * A job arriving from the server.
 *
 * NEWER WINS, PER JOB, AND TWO DESIGNS ARE NEVER MERGED. Contractors do not
 * edit the same house on two devices in the same minute, and anything cleverer
 * costs weeks for a case that does not happen. What must never happen is
 * silently overwriting work that has not been sent yet, so a dirty local copy
 * refuses the incoming one and goes up on the next push instead.
 */
export async function acceptFromServer(row: StoredDoc): Promise<'kept' | 'wrote' | 'skipped'> {
  try {
    const mine = await tx<StoredDoc | undefined>(DOCS, 'readonly', (s) => s.get(row.id))
    if (mine?.dirty) return 'kept'
    if (mine && mine.savedAt >= row.savedAt) return 'skipped'
    await tx(DOCS, 'readwrite', (s) => s.put(row))
    return 'wrote'
  } catch {
    return 'skipped'
  }
}

/** Drop a tombstone for good, once the server has been told about it. */
export async function forgetJob(id: string): Promise<void> {
  try {
    await tx(DOCS, 'readwrite', (s) => s.delete(id))
    await tx(PHOTOS, 'readwrite', (s) => s.delete(id))
  } catch {
    /* it stays a tombstone, which is harmless */
  }
}

/** Ids and versions this browser holds, for working out what to ask for. */
export async function localIndex(): Promise<Map<string, StoredDoc>> {
  try {
    const all = await tx<StoredDoc[]>(DOCS, 'readonly', (s) => s.getAll())
    return new Map(all.map((d) => [d.id, d]))
  } catch {
    return new Map()
  }
}

/**
 * CLAIM WHAT WAS DRAWN BEFORE THERE WERE ACCOUNTS.
 *
 * Somebody has a season in this browser stamped with an invented local id, or
 * with nothing at all, because they drew it before signing in existed. On
 * their first sign in those jobs become theirs and go up with everything else.
 *
 * The alternative is a person signing in, being told their work is synced, and
 * finding a season stranded in one browser. This is the step that gets
 * forgotten until somebody loses one.
 *
 * Jobs stamped with a DIFFERENT real account are left alone. Two people really
 * did share this browser, and handing one of them the other's customers
 * because they signed in first is not a bug anybody recovers from.
 */
export async function claimOrphans(userId: string): Promise<number> {
  try {
    const all = await tx<StoredDoc[]>(DOCS, 'readonly', (s) => s.getAll())
    let n = 0
    for (const d of all) {
      const owner = d.accountId
      if (owner && !owner.startsWith('local_')) continue
      await tx(DOCS, 'readwrite', (s) => s.put({ ...d, accountId: userId, dirty: true }))
      n++
    }
    return n
  } catch {
    return 0
  }
}

/**
 * DELETING IS A CHANGE, NOT AN ABSENCE.
 *
 * This removed the record and the photograph and stopped, which looks right
 * and is wrong the moment there is a server: the row is still up there, so the
 * next pull brings the job straight back and the person who deleted it watches
 * it reappear. There is no way to explain that to somebody.
 *
 * So it leaves a tombstone. The record stays, marked deleted and unsent, and
 * the list filters it out so it is gone as far as anybody can tell. The
 * photograph goes immediately, because that is the megabytes and there is
 * nothing left to look at.
 */
export async function deleteProject(id: string): Promise<void> {
  try {
    const d = await tx<StoredDoc | undefined>(DOCS, 'readonly', (s) => s.get(id))
    if (d) {
      await tx(DOCS, 'readwrite', (s) =>
        s.put({
          ...d,
          deletedAt: new Date().toISOString(),
          savedAt: new Date().toISOString(),
          dirty: true,
        }),
      )
    }
    await tx(PHOTOS, 'readwrite', (s) => s.delete(id))
  } catch {
    /* nothing to do: it either went or it was never there */
  }
}

/**
 * A small JPEG of the house for the project list. Built from the photo rather
 * than the rendered canvas, because it only has to be recognizable and this
 * way it costs nothing and never has to wait on the renderer.
 */
export function makeThumb(dataUrl: string, width = 320): Promise<string | undefined> {
  return new Promise((resolve) => {
    const img = new Image()
    img.onload = () => {
      try {
        const c = document.createElement('canvas')
        const s = width / img.naturalWidth
        c.width = width
        c.height = Math.max(1, Math.round(img.naturalHeight * s))
        c.getContext('2d')!.drawImage(img, 0, 0, c.width, c.height)
        resolve(c.toDataURL('image/jpeg', 0.62))
      } catch {
        resolve(undefined)
      }
    }
    img.onerror = () => resolve(undefined)
    img.src = dataUrl
  })
}

/**
 * One-time rescue of anything left in the old localStorage store, so a job
 * saved before this existed is not lost by the upgrade.
 */
export async function migrateFromLocalStorage(): Promise<number> {
  const INDEX = 'lightsplug.projects.v1'
  let moved = 0
  try {
    const raw = localStorage.getItem(INDEX)
    if (!raw) return 0
    const list = JSON.parse(raw) as { id: string }[]
    for (const p of list) {
      const key = `lightsplug.project.${p.id}`
      const docRaw = localStorage.getItem(key)
      if (!docRaw) continue
      const doc = JSON.parse(docRaw) as LightsOsDocument
      const thumb = doc.photo ? await makeThumb(doc.photo.dataUrl) : undefined
      if ((await saveProject(doc, { thumb })) === 'saved') {
        localStorage.removeItem(key)
        moved++
      }
    }
    localStorage.removeItem(INDEX)
  } catch {
    /* best effort; a failed rescue must never block the app from opening */
  }
  return moved
}

/*
 * BACKUP AND RESTORE, AS A FILE.
 *
 * Every job lives in one browser on one machine. Clear the site data, spill a
 * coffee on the laptop, or take the new phone out of the box, and a season of
 * quotes is gone with no way back - and there is no account to restore from,
 * because there is deliberately no account. That is a fine trade for working
 * offline in a driveway; it is not a fine trade for losing the work.
 *
 * So the whole store writes out as one file the contractor keeps wherever he
 * keeps things, and reads back on any machine. Photos included, which is what
 * makes the file large and also what makes it worth having.
 */
const BACKUP_KIND = 'lightsos.backup'
/** What backup files written before the rename call themselves. Still valid. */
const BACKUP_KIND_OLD = 'lightsplug.backup'
const BACKUP_VERSION = 1

export interface BackupFile {
  kind: typeof BACKUP_KIND | typeof BACKUP_KIND_OLD
  version: number
  savedAt: string
  jobs: { doc: LightsOsDocument; thumb?: string }[]
}

export async function exportBackup(): Promise<BackupFile> {
  const summaries = await listProjects()
  const jobs: BackupFile['jobs'] = []
  for (const s of summaries) {
    const doc = await loadProject(s.id)
    if (doc) jobs.push({ doc, thumb: s.thumb })
  }
  return {
    kind: BACKUP_KIND,
    version: BACKUP_VERSION,
    savedAt: new Date().toISOString(),
    jobs,
  }
}

export interface RestoreResult {
  added: number
  skipped: number
  failed: number
}

/**
 * Restore, without ever overwriting what is already here.
 *
 * A job whose id is already in the database is left alone. Restoring the same
 * backup twice therefore does nothing the second time, and restoring an old
 * backup onto a machine that has newer work cannot silently roll it back -
 * which is the way a restore usually ruins someone's day.
 */
export async function importBackup(file: unknown): Promise<RestoreResult> {
  const out: RestoreResult = { added: 0, skipped: 0, failed: 0 }
  const f = file as BackupFile | null
  // Both names are accepted forever. A file somebody saved the week before the
  // rename is still a season of work, and refusing it over a string would be
  // the app losing data to its own marketing.
  const known = f?.kind === BACKUP_KIND || f?.kind === BACKUP_KIND_OLD
  if (!f || !known || !Array.isArray(f.jobs)) {
    throw new Error('That is not a LightsOS backup file.')
  }
  const existing = new Set((await listProjects()).map((p) => p.id))
  for (const job of f.jobs) {
    const doc = job?.doc
    if (!doc?.id) {
      out.failed++
      continue
    }
    if (existing.has(doc.id)) {
      out.skipped++
      continue
    }
    const res = await saveProject(doc, { thumb: job.thumb })
    if (res === 'saved') out.added++
    else out.failed++
  }
  return out
}

/** Copy a job, so a second design for the same house does not overwrite the first. */
export async function duplicateProject(id: string, newId: string): Promise<boolean> {
  try {
    const doc = await loadProject(id)
    if (!doc) return false
    const thumb = doc.photo ? await makeThumb(doc.photo.dataUrl) : undefined
    const copy: LightsOsDocument = {
      ...doc,
      id: newId,
      name: `${doc.name} copy`,
      status: 'draft',
      createdAt: new Date().toISOString(),
    }
    return (await saveProject(copy, { thumb })) === 'saved'
  } catch {
    return false
  }
}

/** Change just the status, without loading the job into the editor. */
export async function setProjectStatus(id: string, status: JobStatus): Promise<boolean> {
  try {
    const stored = await tx<StoredDoc | undefined>(DOCS, 'readonly', (s) => s.get(id))
    if (!stored) return false
    /*
     * Marked unsent, and stamped now.
     *
     * This wrote straight to the browser and stopped. Moving a job to Approved
     * from the list - which is the one gesture that runs a season - never left
     * the machine it was done on. Silent, and invisible until somebody opened
     * a laptop and found last week's answer.
     */
    const now = new Date().toISOString()
    // And stamped with WHEN it moved, which is the only honest answer this
    // product has to how long a quote has been waiting. See statusAt.
    await tx(DOCS, 'readwrite', (s) =>
      s.put({ ...stored, status, statusAt: now, savedAt: now, dirty: true }),
    )
    return true
  } catch {
    return false
  }
}
